
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import type { CreateEstimateData } from "./useEstimates";
import { EstimateStratigraphy } from "@/types/estimateStratigraphy";
import { usePlanUsage } from "./usePlanUsage";

/**
 * Duplica un intero preventivo (estimate) assieme alle sue stratigrafie (copia snapshot, non referenza!)
 * Opzionalmente consente di assegnare un nome personalizzato.
 */
export const useDuplicateEstimate = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { estimates: estimatesUsage } = usePlanUsage();

  const mutation = useMutation({
    mutationFn: async ({
      estimate,
      estimateStratigraphies,
      newName,
    }: {
      estimate: any;
      estimateStratigraphies: EstimateStratigraphy[];
      newName?: string;
    }) => {
      if (!user) throw new Error("User not authenticated");
      // F33B-3 — gate piano: blocca se il tetto preventivi del piano è raggiunto.
      // Su Solo è 15 totali; Studio/Team illimitati. Il toast fa da CTA upgrade.
      if (estimatesUsage.atLimit) {
        const err = new Error(
          `Hai raggiunto il limite di ${estimatesUsage.max} preventivi del piano Solo. Passa a Studio per illimitati.`,
        );
        (err as Error & { code?: string }).code = "PLAN_LIMIT_REACHED";
        throw err;
      }

      // 1. Crea nuovo estimate (bozza) con nome incrementale/nome selezionato.
      // Naming: invece di concatenare "(Copia)(Copia)(Copia)" all'infinito, ripulisco
      // i suffissi esistenti e applico "(Copia 2)", "(Copia 3)", ecc.
      let name = newName;
      if (!name) {
        const base = String(estimate.name).replace(/\s*\((?:Copia(?:\s\d+)?)\)+\s*$/g, '').trim();
        const m = String(estimate.name).match(/\(Copia(?:\s(\d+))?\)\s*$/);
        const nextN = m ? (m[1] ? parseInt(m[1], 10) + 1 : 2) : 1;
        name = nextN === 1 ? `${base} (Copia)` : `${base} (Copia ${nextN})`;
      }
      const { data: createdEstimate, error: estimateError } = await supabase
        .from("estimates")
        .insert({
          name,
          description: estimate.description,
          project_id: estimate.projectId,
          status: "draft",
          version: estimate.version + 1,
          total_amount: 0,
        })
        .select()
        .single();

      if (estimateError || !createdEstimate) {
        throw estimateError || new Error("Errore nella duplicazione preventivo");
      }

      // 2. Recupera tutte le stratigrafie collegate (snapshot)
      // 3. Per ciascuna, crea una nuova estimate_stratigraphy snapshot collegata al nuovo estimate
      for (const strat of estimateStratigraphies) {
        const { error: stratError } = await supabase
          .from("estimate_stratigraphies")
          .insert({
            estimate_id: createdEstimate.id,
            stratigraphy_id: null,
            name: strat.name,
            description: strat.description,
            area: strat.area,
            quantity: strat.quantity,
            unit_cost: strat.unitCost,
            total_cost: strat.totalCost,
            stratigraphy_data: strat.stratigraphyData || null,
            layers_data: strat.layersData || null,
            prices_updated_at: strat.pricesUpdatedAt
              ? new Date(strat.pricesUpdatedAt).toISOString()
              : new Date().toISOString(),
            is_snapshot: true,
            original_stratigraphy_id: strat.stratigraphyId || null,
            finish_level: (strat as any).finishLevel ?? undefined,
            wall_height: (strat as any).wallHeight ?? undefined,
            calculated_materials: (strat as any).calculatedMaterials ?? undefined,
          });

        if (stratError) {
          throw stratError;
        }
      }

      return createdEstimate;
    },
    onSuccess: (createdEstimate) => {
      queryClient.invalidateQueries({ queryKey: ["estimates"] });
      // F33B-3: invalidate plan-usage così il counter preventivi si aggiorna live
      // e il prossimo click su Duplica blocca correttamente se siamo al tetto.
      queryClient.invalidateQueries({ queryKey: ["plan-usage"] });
      toast.success("Preventivo duplicato con successo!");
    },
    onError: (error: Error & { code?: string }) => {
      if (error.code === "PLAN_LIMIT_REACHED") {
        // Messaggio dedicato + CTA upgrade. Apriamo il link a billing nel toast.
        toast.error("Limite preventivi raggiunto", {
          description: error.message,
          action: {
            label: "Passa a Studio",
            onClick: () => { window.location.href = "/settings/billing"; },
          },
        });
      } else {
        toast.error("Si è verificato un errore durante la duplicazione del preventivo");
      }
      console.error("[useDuplicateEstimate] error:", error);
    },
  });

  return {
    duplicateEstimate: mutation.mutate,
    isDuplicating: mutation.isPending,
  };
};
